import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Avatar, Chip, IconButton, Button,
  TextField, InputAdornment, Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem, CircularProgress, Alert, Snackbar,
  Tooltip, Divider, ToggleButton, ToggleButtonGroup, Autocomplete
} from '@mui/material';
import {
  Add as AddIcon, Search as SearchIcon, Edit as EditIcon, Delete as DeleteIcon,
  CalendarToday as CalendarIcon, ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon, Today as TodayIcon, Refresh as RefreshIcon,
  Close as CloseIcon, AccessTime as TimeIcon, Person as PersonIcon,
  CheckCircle as CheckCircleIcon, Cancel as CancelIcon,
  PlayArrow as PlayIcon, Done as DoneIcon, EventBusy as NoShowIcon,
  Schedule as ScheduleIcon, PersonAdd as PersonAddIcon,
} from '@mui/icons-material';
import axios from '../../config/axios';
import { getApiUrl } from '../../config/environment';
import { useClinicPageBreakpoints, clinicPageContainerSx } from '../../hooks/useClinicPageBreakpoints';
import TeleconsultButton from '../../components/Teleconsulta/TeleconsultButton';


const statusLabels: Record<string, string> = {
  scheduled: 'Agendado', confirmed: 'Confirmado', waiting: 'Na Espera',
  in_progress: 'Em Atendimento', completed: 'Concluído', cancelled: 'Cancelado',
  no_show: 'Não Compareceu', rescheduled: 'Reagendado',
};

const statusColors: Record<string, { bg: string; color: string }> = {
  scheduled: { bg: '#e0e7ff', color: '#4338ca' }, confirmed: { bg: '#d1fae5', color: '#059669' },
  waiting: { bg: '#fef3c7', color: '#d97706' }, in_progress: { bg: '#dbeafe', color: '#2563eb' },
  completed: { bg: '#d1fae5', color: '#047857' }, cancelled: { bg: '#fee2e2', color: '#dc2626' },
  no_show: { bg: '#fef3c7', color: '#b45309' }, rescheduled: { bg: '#e0e7ff', color: '#6366f1' },
};

const typeLabels: Record<string, string> = {
  consultation: 'Consulta', return: 'Retorno', procedure: 'Procedimento',
  exam: 'Exame', emergency: 'Emergência', surgery: 'Cirurgia',
};

const Appointments: React.FC = () => {
  const { isMobile } = useClinicPageBreakpoints();

  const [appointments, setAppointments] = useState<any[]>([]);
  const [patients, setPatients] = useState<any[]>([]);
  const [professionals, setProfessionals] = useState<any[]>([]);
  const [rooms, setRooms] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [viewMode, setViewMode] = useState<'day' | 'week'>('day');
  const [professionalFilter, setProfessionalFilter] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  const [formData, setFormData] = useState({
    patient_id: '', professional_id: '', room_id: '', appointment_date: selectedDate,
    start_time: '08:00', duration_minutes: 30, appointment_type: 'consultation',
    billing_mode: 'particular' as 'particular' | 'plano',
    title: '', notes: '', internal_notes: '',
  });

  const [quickPatientOpen, setQuickPatientOpen] = useState(false);
  const [quickPatientSaving, setQuickPatientSaving] = useState(false);
  const [quickPatient, setQuickPatient] = useState({ full_name: '', cpf: '', phone: '55', whatsapp: '55', email: '', birth_date: '' });

  const fetchAppointments = useCallback(async () => {
    try {
      setLoading(true);
      const params: any = { date_from: selectedDate, date_to: selectedDate };
      if (professionalFilter) params.professional_id = professionalFilter;

      const response = await axios.get(getApiUrl('/clinic/appointments'), { params });
      if (response.data.success) setAppointments(response.data.data || []);
    } catch (err) {
      console.error('Error fetching appointments:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedDate, professionalFilter]);

  const fetchAuxData = async () => {
    try {
      const [patientsRes, professionalsRes, roomsRes] = await Promise.all([
        axios.get(getApiUrl('/clinic/patients'), { params: { limit: 500 } }),
        axios.get(getApiUrl('/clinic/professionals'), { params: { is_active: true } }),
        axios.get(getApiUrl('/clinic/rooms')),
      ]);
      setPatients(patientsRes.data.data || []);
      setProfessionals(professionalsRes.data.data || []);
      setRooms(roomsRes.data.data || []);
    } catch (err) {
      console.error('Error fetching aux data:', err);
    }
  };

  useEffect(() => { fetchAppointments(); }, [fetchAppointments]);
  useEffect(() => { fetchAuxData(); }, []);

  const handleQuickPatientSave = async () => {
    if (!quickPatient.full_name.trim()) {
      setSnackbar({ open: true, message: 'Nome do paciente é obrigatório', severity: 'error' }); return;
    }
    try {
      setQuickPatientSaving(true);
      const payload: any = { full_name: quickPatient.full_name.trim() };
      if (quickPatient.cpf.trim()) payload.cpf = quickPatient.cpf.trim();
      if (quickPatient.phone.trim() && quickPatient.phone.trim() !== '55') payload.phone = quickPatient.phone.trim();
      if (quickPatient.whatsapp.trim() && quickPatient.whatsapp.trim() !== '55') payload.whatsapp = quickPatient.whatsapp.trim();
      if (quickPatient.email.trim()) payload.email = quickPatient.email.trim();
      if (quickPatient.birth_date) payload.birth_date = quickPatient.birth_date;
      const { data } = await axios.post(getApiUrl('/clinic/patients'), payload);
      const newPatient = data.data || data;
      setPatients(prev => [newPatient, ...prev]);
      setFormData(prev => ({ ...prev, patient_id: newPatient.id }));
      setQuickPatientOpen(false);
      setQuickPatient({ full_name: '', cpf: '', phone: '55', whatsapp: '55', email: '', birth_date: '' });
      setSnackbar({ open: true, message: 'Paciente cadastrado e selecionado!', severity: 'success' });
    } catch (e: any) {
      setSnackbar({ open: true, message: e.response?.data?.error || 'Erro ao cadastrar paciente', severity: 'error' });
    } finally {
      setQuickPatientSaving(false);
    }
  };

  const handleSave = async () => {
    if (!formData.patient_id || !formData.professional_id || !formData.appointment_date || !formData.start_time) {
      setSnackbar({ open: true, message: 'Paciente, profissional, data e horário são obrigatórios', severity: 'error' });
      return;
    }
    try {
      setSaving(true);
      if (editingId) {
        await axios.put(getApiUrl(`/clinic/appointments/${editingId}`), formData);
        setSnackbar({ open: true, message: 'Agendamento atualizado!', severity: 'success' });
      } else {
        await axios.post(getApiUrl('/clinic/appointments'), formData);
        setSnackbar({ open: true, message: 'Agendamento criado!', severity: 'success' });
      }
      setDialogOpen(false);
      fetchAppointments();
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Erro ao salvar', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const updateStatus = async (id: string, status: string) => {
    try {
      await axios.patch(getApiUrl(`/clinic/appointments/${id}/status`), { status });
      setSnackbar({ open: true, message: `Status atualizado: ${statusLabels[status]}`, severity: 'success' });
      fetchAppointments();
    } catch (err) {
      setSnackbar({ open: true, message: 'Erro ao atualizar status', severity: 'error' });
    }
  };

  const changeDate = (days: number) => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() + days);
    setSelectedDate(d.toISOString().split('T')[0]);
  };

  const timeSlots = Array.from({ length: 28 }, (_, i) => {
    const hour = Math.floor(i / 2) + 7;
    const min = i % 2 === 0 ? '00' : '30';
    return `${String(hour).padStart(2, '0')}:${min}`;
  });

  const formatDateDisplay = (dateStr: string) => {
    const d = new Date(dateStr + 'T12:00:00');
    return d.toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  };

  const openNewAppointment = (time?: string) => {
    setFormData({
      patient_id: '', professional_id: '', room_id: '', appointment_date: selectedDate,
      start_time: time || '08:00', duration_minutes: 30, appointment_type: 'consultation',
      billing_mode: 'particular',
      title: '', notes: '', internal_notes: '',
    });
    setEditingId(null);
    setDialogOpen(true);
  };

  return (
    <Box sx={clinicPageContainerSx}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#1e1b4b' }}>
            📅 Agenda de Consultas
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => openNewAppointment()}
          sx={{ bgcolor: '#14b8a6', '&:hover': { bgcolor: '#0d9488' }, borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>
          Novo Agendamento
        </Button>
      </Box>

      {/* Controls */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center', flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={() => changeDate(-1)} size="small"><ChevronLeftIcon /></IconButton>
          <Button onClick={() => setSelectedDate(new Date().toISOString().split('T')[0])} size="small"
            sx={{ textTransform: 'none', fontWeight: 600 }} startIcon={<TodayIcon />}>
            Hoje
          </Button>
          <IconButton onClick={() => changeDate(1)} size="small"><ChevronRightIcon /></IconButton>
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#374151', textTransform: 'capitalize', flex: 1 }}>
          {formatDateDisplay(selectedDate)}
        </Typography>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Profissional</InputLabel>
          <Select value={professionalFilter} label="Profissional" onChange={(e) => setProfessionalFilter(e.target.value)}>
            <MenuItem value="">Todos</MenuItem>
            {professionals.map((p) => <MenuItem key={p.id} value={p.id}>{p.full_name}</MenuItem>)}
          </Select>
        </FormControl>
        <Tooltip title="Atualizar"><IconButton onClick={fetchAppointments}><RefreshIcon /></IconButton></Tooltip>
      </Box>

      {/* Timeline View */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress sx={{ color: '#8b5cf6' }} />
        </Box>
      ) : (
        <Card sx={{ borderRadius: 3, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
          <CardContent sx={{ p: 0 }}>
            {timeSlots.map((time) => {
              const slotAppointments = appointments.filter((apt) => apt.start_time?.substring(0, 5) === time);
              const isFullHour = time.endsWith(':00');

              return (
                <Box key={time} sx={{
                  display: 'flex', minHeight: slotAppointments.length > 0 ? 80 : 40,
                  borderBottom: '1px solid #f3f4f6',
                  '&:hover': { bgcolor: '#faf5ff' },
                  cursor: 'pointer',
                }} onClick={() => slotAppointments.length === 0 ? openNewAppointment(time) : null}>
                  {/* Time label */}
                  <Box sx={{
                    width: 80, display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
                    pt: 1, borderRight: '1px solid #e5e7eb', bgcolor: '#f9fafb',
                  }}>
                    <Typography variant="body2" sx={{
                      fontWeight: isFullHour ? 700 : 400,
                      color: isFullHour ? '#374151' : '#9ca3af',
                      fontSize: isFullHour ? 14 : 12,
                    }}>
                      {time}
                    </Typography>
                  </Box>

                  {/* Appointments */}
                  <Box sx={{ flex: 1, display: 'flex', gap: 1, p: 0.5, flexWrap: 'wrap' }}>
                    {slotAppointments.map((apt) => (
                      <Box key={apt.id} sx={{
                        flex: 1, minWidth: 200, p: 1.5, borderRadius: 2,
                        bgcolor: apt.clinic_professionals?.color ? `${apt.clinic_professionals.color}15` : '#f3e8ff',
                        borderLeft: `4px solid ${apt.clinic_professionals?.color || '#8b5cf6'}`,
                        cursor: 'pointer',
                        '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.1)' },
                      }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e1b4b' }}>
                              {apt.clinic_patients?.full_name || 'Paciente'}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#6b7280' }}>
                              {apt.start_time?.substring(0, 5)} - {apt.end_time?.substring(0, 5)} • Dr(a). {apt.clinic_professionals?.full_name}
                            </Typography>
                            <br />
                            <Typography variant="caption" sx={{ color: '#6b7280' }}>
                              {typeLabels[apt.appointment_type] || apt.appointment_type}
                              {apt.clinic_rooms?.name ? ` • ${apt.clinic_rooms.name}` : ''}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                            <Chip label={statusLabels[apt.status] || apt.status} size="small" sx={{
                              bgcolor: statusColors[apt.status]?.bg || '#f3f4f6',
                              color: statusColors[apt.status]?.color || '#6b7280',
                              fontWeight: 600, fontSize: 10,
                            }} />
                          </Box>
                        </Box>
                        {/* Quick Actions */}
                        <Box sx={{ display: 'flex', gap: 0.5, mt: 1 }}>
                          {apt.status === 'scheduled' && (
                            <Tooltip title="Confirmar"><IconButton size="small" onClick={(e) => { e.stopPropagation(); updateStatus(apt.id, 'confirmed'); }}>
                              <CheckCircleIcon sx={{ fontSize: 16, color: '#10b981' }} /></IconButton></Tooltip>
                          )}
                          {(apt.status === 'confirmed' || apt.status === 'scheduled') && (
                            <Tooltip title="Check-in"><IconButton size="small" onClick={(e) => { e.stopPropagation(); updateStatus(apt.id, 'waiting'); }}>
                              <ScheduleIcon sx={{ fontSize: 16, color: '#f59e0b' }} /></IconButton></Tooltip>
                          )}
                          {apt.status === 'waiting' && (
                            <Tooltip title="Iniciar"><IconButton size="small" onClick={(e) => { e.stopPropagation(); updateStatus(apt.id, 'in_progress'); }}>
                              <PlayIcon sx={{ fontSize: 16, color: '#2563eb' }} /></IconButton></Tooltip>
                          )}
                          {apt.status === 'in_progress' && (
                            <Tooltip title="Concluir"><IconButton size="small" onClick={(e) => { e.stopPropagation(); updateStatus(apt.id, 'completed'); }}>
                              <DoneIcon sx={{ fontSize: 16, color: '#059669' }} /></IconButton></Tooltip>
                          )}
                          {apt.status !== 'completed' && apt.status !== 'cancelled' && (
                            <>
                              <Tooltip title="Cancelar"><IconButton size="small" onClick={(e) => { e.stopPropagation(); updateStatus(apt.id, 'cancelled'); }}>
                                <CancelIcon sx={{ fontSize: 16, color: '#ef4444' }} /></IconButton></Tooltip>
                              <Tooltip title="Não compareceu"><IconButton size="small" onClick={(e) => { e.stopPropagation(); updateStatus(apt.id, 'no_show'); }}>
                                <NoShowIcon sx={{ fontSize: 16, color: '#d97706' }} /></IconButton></Tooltip>
                            </>
                          )}
                          <Box onClick={(e) => e.stopPropagation()} sx={{ ml: 'auto' }}>
                            <TeleconsultButton appointmentId={apt.id} appointmentStatus={apt.status} compact />
                          </Box>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* New/Edit Appointment Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', justifyContent: 'space-between' }}>
          {editingId ? 'Editar Agendamento' : 'Novo Agendamento'}
          <IconButton onClick={() => setDialogOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Autocomplete
                options={patients}
                getOptionLabel={(p: any) => `${p.full_name}${p.cpf ? ` (${p.cpf})` : ''}`}
                value={patients.find(p => p.id === formData.patient_id) || null}
                onChange={(_, v) => setFormData({ ...formData, patient_id: v?.id || '' })}
                renderInput={(params) => <TextField {...params} label="Paciente *" size="small" />}
              />
              <Button size="small" startIcon={<PersonAddIcon />}
                onClick={() => { setQuickPatient({ full_name: '', cpf: '', phone: '55', whatsapp: '55', email: '', birth_date: '' }); setQuickPatientOpen(true); }}
                sx={{ mt: 0.5, textTransform: 'none', fontSize: '0.78rem', color: '#14B8A6', fontWeight: 600 }}>
                Paciente não encontrado? Cadastrar novo
              </Button>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Profissional *</InputLabel>
                <Select value={formData.professional_id} label="Profissional *" onChange={(e) => setFormData({ ...formData, professional_id: e.target.value })}>
                  {professionals.map((p) => <MenuItem key={p.id} value={p.id}>{p.full_name} - {p.specialty}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Sala</InputLabel>
                <Select value={formData.room_id} label="Sala" onChange={(e) => setFormData({ ...formData, room_id: e.target.value })}>
                  <MenuItem value="">Nenhuma</MenuItem>
                  {rooms.map((r) => <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Data *" type="date" value={formData.appointment_date}
                onChange={(e) => setFormData({ ...formData, appointment_date: e.target.value })}
                size="small" InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Horário *" type="time" value={formData.start_time}
                onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                size="small" InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Duração (min)</InputLabel>
                <Select value={formData.duration_minutes} label="Duração (min)" onChange={(e) => setFormData({ ...formData, duration_minutes: Number(e.target.value) })}>
                  {[15, 30, 45, 60, 90, 120].map((d) => <MenuItem key={d} value={d}>{d} min</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Tipo</InputLabel>
                <Select value={formData.appointment_type} label="Tipo" onChange={(e) => setFormData({ ...formData, appointment_type: e.target.value })}>
                  {Object.entries(typeLabels).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Título" value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })} size="small" />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Cobrança</InputLabel>
                <Select
                  value={formData.billing_mode}
                  label="Cobrança"
                  onChange={(e) => setFormData({ ...formData, billing_mode: e.target.value as 'particular' | 'plano' })}
                >
                  <MenuItem value="particular">Particular</MenuItem>
                  <MenuItem value="plano">Plano</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Observações" value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })} size="small" multiline rows={2} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Notas Internas" value={formData.internal_notes}
                onChange={(e) => setFormData({ ...formData, internal_notes: e.target.value })} size="small" multiline rows={2}
                helperText="Visível apenas para a equipe" />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ textTransform: 'none' }}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}
            sx={{ bgcolor: '#14b8a6', '&:hover': { bgcolor: '#0d9488' }, textTransform: 'none', fontWeight: 600 }}>
            {saving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : editingId ? 'Atualizar' : 'Agendar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Quick Patient Creation Dialog */}
      <Dialog open={quickPatientOpen} onClose={() => !quickPatientSaving && setQuickPatientOpen(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonAddIcon sx={{ color: '#14B8A6' }} />
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem' }}>Cadastro Rápido de Paciente</Typography>
          </Box>
          <IconButton onClick={() => !quickPatientSaving && setQuickPatientOpen(false)} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Nome Completo *" value={quickPatient.full_name}
                onChange={e => setQuickPatient(p => ({ ...p, full_name: e.target.value }))} disabled={quickPatientSaving} autoFocus />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="CPF" value={quickPatient.cpf}
                onChange={e => setQuickPatient(p => ({ ...p, cpf: e.target.value }))} disabled={quickPatientSaving} placeholder="000.000.000-00" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Data de Nascimento" type="date" value={quickPatient.birth_date}
                onChange={e => setQuickPatient(p => ({ ...p, birth_date: e.target.value }))} disabled={quickPatientSaving} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Telefone" value={quickPatient.phone}
                onChange={e => setQuickPatient(p => ({ ...p, phone: e.target.value }))} disabled={quickPatientSaving} placeholder="5511999999999" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="WhatsApp" value={quickPatient.whatsapp}
                onChange={e => setQuickPatient(p => ({ ...p, whatsapp: e.target.value }))} disabled={quickPatientSaving} placeholder="5511999999999" />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="E-mail" type="email" value={quickPatient.email}
                onChange={e => setQuickPatient(p => ({ ...p, email: e.target.value }))} disabled={quickPatientSaving} placeholder="paciente@email.com" />
            </Grid>
          </Grid>
          <Alert severity="info" sx={{ mt: 2, borderRadius: 2, fontSize: '0.78rem' }}>
            Dados adicionais podem ser preenchidos depois na ficha completa do paciente.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button onClick={() => setQuickPatientOpen(false)} disabled={quickPatientSaving} sx={{ color: '#64748b' }}>Cancelar</Button>
          <Button variant="contained" onClick={handleQuickPatientSave} disabled={quickPatientSaving || !quickPatient.full_name.trim()}
            startIcon={quickPatientSaving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <PersonAddIcon />}
            sx={{ bgcolor: '#14B8A6', '&:hover': { bgcolor: '#0d9488' }, textTransform: 'none', fontWeight: 600 }}>
            {quickPatientSaving ? 'Cadastrando...' : 'Cadastrar e Selecionar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default Appointments;
